// Journal — the accountant's working surface. List entries by date, source,
// and search; open the lines; post, change, delete, or reverse; enter opening
// balances. The database refuses anything unbalanced or inside a closed month
// and says why in plain words, which this tab shows as is.
import { useCallback, useEffect, useMemo, useState } from 'react';
import { useBooksAccess } from '../../contexts/AuthContext';
import { ConfirmButton, Empty, ErrorNote, Field, Note, Section } from '../../components/ui';
import { day, dateTime, memberWords } from '../../lib/format';
import { journal } from '../../lib/ledger/api';
import { MEMBERS, SOURCE_LABELS, type Account, type EntrySource, type JournalEntry, type JournalPage, type LineInput, type Member } from '../../lib/ledger/types';
import { AccountSelect, BackTo, Money, MoneyInput, ReadOnlyNote, downloadCsv, today, useAccounts, yearStart } from './shared';

const PAGE_SIZE = 200;
const SOURCES = Object.keys(SOURCE_LABELS) as EntrySource[];

type EditorMode =
  | { kind: 'new' }
  | { kind: 'edit'; entry: JournalEntry }
  | { kind: 'opening' };

function entryTotal(e: JournalEntry): number {
  return (e.lines ?? []).reduce((s, l) => s + Number(l.debit_cents ?? 0), 0);
}

function canChange(e: JournalEntry): boolean {
  return (e.source === 'manual' || e.source === 'opening') && !e.is_reversed;
}

export function JournalTab() {
  const { canEdit } = useBooksAccess();
  const chart = useAccounts();

  const [from, setFrom] = useState(yearStart());
  const [to, setTo] = useState(today());
  const [source, setSource] = useState<EntrySource | ''>('');
  const [search, setSearch] = useState('');
  const [searchApplied, setSearchApplied] = useState('');
  const [offset, setOffset] = useState(0);

  const [page, setPage] = useState<JournalPage | null>(null);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState<unknown>(null);
  const [open, setOpen] = useState<Set<string>>(new Set());
  const [editor, setEditor] = useState<EditorMode | null>(null);
  const [reversing, setReversing] = useState<string | null>(null);
  const [done, setDone] = useState<string | null>(null);
  const [actErr, setActErr] = useState<unknown>(null);
  const [tick, setTick] = useState(0);

  // Search applies after a short pause so each keystroke does not hit the database.
  useEffect(() => {
    const t = setTimeout(() => { setSearchApplied(search.trim()); setOffset(0); }, 350);
    return () => clearTimeout(t);
  }, [search]);

  const load = useCallback(async () => {
    setLoading(true);
    setErr(null);
    try {
      const p = await journal.page({ from, to, source: source || null, search: searchApplied || null, limit: PAGE_SIZE, offset });
      setPage(p ?? { total: 0, entries: [] });
    } catch (e) {
      setErr(e);
      setPage(null);
    } finally {
      setLoading(false);
    }
  }, [from, to, source, searchApplied, offset]);

  useEffect(() => { void load(); }, [load, tick]);

  const entries = page?.entries ?? [];
  const total = page?.total ?? 0;

  function toggle(id: string) {
    setOpen((s) => {
      const n = new Set(s);
      if (n.has(id)) n.delete(id); else n.add(id);
      return n;
    });
  }

  async function act(label: string, fn: () => Promise<unknown>) {
    setActErr(null);
    setDone(null);
    try {
      await fn();
      setDone(label);
      setReversing(null);
      setTick((t) => t + 1);
    } catch (e) {
      setActErr(e);
    }
  }

  function csv() {
    const rows: unknown[][] = [['Date', 'Memo', 'Source', 'Reference', 'Account code', 'Account', 'Debit', 'Credit', 'Member', 'Line memo', 'Reversed']];
    for (const e of entries) {
      for (const l of e.lines ?? []) {
        const a = chart.byId.get(l.account_id);
        rows.push([
          e.entry_date, e.memo ?? '', SOURCE_LABELS[e.source] ?? e.source, e.source_ref ?? '',
          l.code ?? a?.code ?? '', l.name ?? a?.name ?? '',
          l.debit_cents ? (l.debit_cents / 100).toFixed(2) : '', l.credit_cents ? (l.credit_cents / 100).toFixed(2) : '',
          l.member ? memberWords(l.member) : '', l.memo ?? '', e.is_reversed ? 'yes' : '',
        ]);
      }
    }
    downloadCsv(`rootegra-journal-${from}-to-${to}.csv`, rows);
  }

  // ── Editor drill-in ────────────────────────────────────────────────────────
  if (editor) {
    return (
      <>
        <BackTo label="Journal" onClick={() => setEditor(null)} />
        <EntryEditor
          mode={editor}
          accounts={chart.accounts}
          onCancel={() => setEditor(null)}
          onSaved={(label) => {
            setEditor(null);
            setDone(label);
            setTick((t) => t + 1);
          }}
        />
      </>
    );
  }

  return (
    <>
      {!canEdit && <ReadOnlyNote />}
      <div className="lp-row lp-no-print" style={{ marginBottom: 14 }}>
        <button type="button" className="lp-btn lp-btn--primary" disabled={!canEdit || chart.loading} onClick={() => setEditor({ kind: 'new' })}>New entry</button>
        <button type="button" className="lp-btn" disabled={!canEdit || chart.loading} onClick={() => setEditor({ kind: 'opening' })}>Opening balances</button>
        <button type="button" className="lp-btn lp-btn--quiet" disabled={entries.length === 0} onClick={csv}>Download CSV</button>
      </div>
      <ErrorNote error={chart.error} />
      {done && <Note tone="good">{done}</Note>}
      <ErrorNote error={actErr} />

      <div className="lp-form-grid lp-no-print" style={{ maxWidth: 1100 }}>
        <Field label="From"><input className="lp-inp" type="date" value={from} onChange={(e) => { if (e.target.value) { setFrom(e.target.value); setOffset(0); } }} /></Field>
        <Field label="To"><input className="lp-inp" type="date" value={to} onChange={(e) => { if (e.target.value) { setTo(e.target.value); setOffset(0); } }} /></Field>
        <Field label="Source">
          <select className="lp-sel" value={source} onChange={(e) => { setSource(e.target.value as EntrySource | ''); setOffset(0); }}>
            <option value="">All sources</option>
            {SOURCES.map((s) => <option key={s} value={s}>{SOURCE_LABELS[s]}</option>)}
          </select>
        </Field>
        <Field label="Search" hint="Memo, reference, or account name."><input className="lp-inp" value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Search entries" /></Field>
      </div>

      <ErrorNote error={err} notConfigured="The ledger is not set up yet." />
      {loading && !page && <Empty>Loading…</Empty>}
      {page && entries.length === 0 && <Empty>No entries between {day(from)} and {day(to)}{source ? ` from ${SOURCE_LABELS[source]}` : ''}{searchApplied ? ` matching “${searchApplied}”` : ''}.</Empty>}

      {entries.length > 0 && (
        <Section
          title="Entries"
          right={<span className="muted">Showing {offset + 1}–{offset + entries.length} of {total}</span>}
        >
          <div className="lp-tbl-wrap">
            <table className="lp-tbl">
              <thead>
                <tr>
                  <th>Date</th>
                  <th>Memo</th>
                  <th>Source</th>
                  <th className="num">Total</th>
                  <th></th>
                </tr>
              </thead>
              <tbody>
                {entries.map((e) => {
                  const isOpen = open.has(e.id);
                  return (
                    <EntryRows
                      key={e.id}
                      entry={e}
                      open={isOpen}
                      onToggle={() => toggle(e.id)}
                      canEdit={canEdit}
                      reversing={reversing === e.id}
                      onReverseStart={() => setReversing(e.id)}
                      onReverseCancel={() => setReversing(null)}
                      onReverse={(date, reason) => act('Reversed.', () => journal.reverse(e.id, date, reason))}
                      onEdit={() => setEditor({ kind: 'edit', entry: e })}
                      onDelete={() => act('Deleted.', () => journal.remove(e.id))}
                    />
                  );
                })}
              </tbody>
            </table>
          </div>
          {total > PAGE_SIZE && (
            <div className="lp-row lp-no-print" style={{ marginTop: 12 }}>
              <button type="button" className="lp-btn lp-btn--small" disabled={offset === 0 || loading} onClick={() => setOffset(Math.max(0, offset - PAGE_SIZE))}>Previous</button>
              <button type="button" className="lp-btn lp-btn--small" disabled={offset + PAGE_SIZE >= total || loading} onClick={() => setOffset(offset + PAGE_SIZE)}>Next</button>
            </div>
          )}
        </Section>
      )}
    </>
  );
}

// ── One entry: the summary row plus, when open, its lines and actions ───────
function EntryRows({ entry: e, open, onToggle, canEdit, reversing, onReverseStart, onReverseCancel, onReverse, onEdit, onDelete }: {
  entry: JournalEntry;
  open: boolean;
  onToggle: () => void;
  canEdit: boolean;
  reversing: boolean;
  onReverseStart: () => void;
  onReverseCancel: () => void;
  onReverse: (date: string, reason: string) => Promise<void>;
  onEdit: () => void;
  onDelete: () => Promise<void>;
}) {
  const [revDate, setRevDate] = useState(today());
  const [revReason, setRevReason] = useState('');
  const changeable = canChange(e);
  return (
    <>
      <tr className={e.is_reversed ? 'quiet' : ''}>
        <td style={{ whiteSpace: 'nowrap' }}>{day(e.entry_date)}</td>
        <td>
          {e.memo || <span className="muted">No memo</span>}
          {e.is_reversed && <> <span className="lp-tag lp-tag--warn">Reversed</span></>}
          {e.reverses_id && <> <span className="lp-tag">Reverses an earlier entry</span></>}
        </td>
        <td><span className="lp-tag">{SOURCE_LABELS[e.source] ?? e.source}</span></td>
        <td className="num"><Money cents={entryTotal(e)} /></td>
        <td style={{ whiteSpace: 'nowrap' }}>
          <button type="button" className="lp-btn lp-btn--quiet lp-btn--small" aria-expanded={open} onClick={onToggle}>
            {open ? 'Hide lines' : `${e.lines?.length ?? 0} lines`}
          </button>
        </td>
      </tr>
      {open && (
        <tr>
          <td colSpan={5} style={{ background: 'var(--parchment)' }}>
            <div className="lp-tbl-wrap">
              <table className="lp-tbl" style={{ fontSize: 15 }}>
                <thead>
                  <tr>
                    <th>Account</th>
                    <th className="num">Debit</th>
                    <th className="num">Credit</th>
                    <th>Member</th>
                    <th>Line memo</th>
                  </tr>
                </thead>
                <tbody>
                  {(e.lines ?? []).slice().sort((a, b) => a.line_no - b.line_no).map((l) => (
                    <tr key={l.id}>
                      <td>{l.code ? `${l.code} · ` : ''}{l.name ?? l.account_id}</td>
                      <td className="num"><Money cents={l.debit_cents} blankZero /></td>
                      <td className="num"><Money cents={l.credit_cents} blankZero /></td>
                      <td>{l.member ? memberWords(l.member) : <span className="muted">—</span>}</td>
                      <td>{l.memo || <span className="muted">—</span>}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            <p className="lp-field-hint" style={{ margin: '8px 0' }}>
              Posted {dateTime(e.created_at)}{e.source_ref ? ` · reference ${e.source_ref}` : ''}
            </p>
            <div className="lp-actions lp-no-print">
              <button type="button" className="lp-btn lp-btn--small" disabled={!canEdit || !changeable} onClick={onEdit} title={changeable ? undefined : 'Only manual and opening entries can be changed. Reverse the others.'}>Edit</button>
              <ConfirmButton
                label="Delete"
                tone="danger"
                disabled={!canEdit || !changeable}
                sentence="Delete this entry for good. Only a manual or opening entry in an open month can be deleted."
                yes="Yes, delete it"
                onConfirm={onDelete}
              />
              {!reversing ? (
                <button type="button" className="lp-btn lp-btn--small" disabled={!canEdit || e.is_reversed} onClick={onReverseStart}>Reverse</button>
              ) : (
                <div className="lp-form" style={{ width: '100%' }}>
                  <div className="lp-form-grid">
                    <Field label="Reverse on"><input className="lp-inp" type="date" value={revDate} onChange={(ev) => setRevDate(ev.target.value)} /></Field>
                    <Field label="Reason" hint="Required. It becomes the memo of the mirror entry."><input className="lp-inp" value={revReason} onChange={(ev) => setRevReason(ev.target.value)} /></Field>
                  </div>
                  <div className="lp-row">
                    <ConfirmButton
                      label="Reverse this entry"
                      tone="primary"
                      disabled={!canEdit || !revReason.trim() || !revDate}
                      sentence={`This posts a mirror entry dated ${day(revDate)} and marks the original as reversed. Both stay in the books.`}
                      yes="Yes, reverse it"
                      onConfirm={() => onReverse(revDate, revReason.trim())}
                    />
                    <button type="button" className="lp-btn lp-btn--quiet" onClick={onReverseCancel}>Cancel</button>
                  </div>
                </div>
              )}
              {!changeable && !e.is_reversed && (
                <span className="lp-field-hint">This entry was posted by the system. To undo it, reverse it.</span>
              )}
            </div>
          </td>
        </tr>
      )}
    </>
  );
}

// ── The multi-line editor (new / edit / opening balances) ───────────────────
type DraftLine = { key: number; account_id: string; debit: number | null; credit: number | null; memo: string; member: '' | Member };

let nextKey = 1;
function blankLine(): DraftLine {
  return { key: nextKey++, account_id: '', debit: null, credit: null, memo: '', member: '' };
}
function isEmptyLine(l: DraftLine): boolean {
  return !l.account_id && !l.debit && !l.credit && !l.memo.trim();
}

function EntryEditor({ mode, accounts, onCancel, onSaved }: {
  mode: EditorMode;
  accounts: Account[];
  onCancel: () => void;
  onSaved: (label: string) => void;
}) {
  const isOpening = mode.kind === 'opening';
  const initial = mode.kind === 'edit' ? mode.entry : null;
  const [date, setDate] = useState(initial?.entry_date ?? today());
  const [memo, setMemo] = useState(initial?.memo ?? (isOpening ? 'Opening balances' : ''));
  const [lines, setLines] = useState<DraftLine[]>(() =>
    initial
      ? initial.lines.slice().sort((a, b) => a.line_no - b.line_no).map((l) => ({
        key: nextKey++, account_id: l.account_id, debit: l.debit_cents || null, credit: l.credit_cents || null, memo: l.memo ?? '', member: l.member ?? '',
      }))
      : [blankLine(), blankLine()],
  );
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<unknown>(null);

  const active = useMemo(() => lines.filter((l) => !isEmptyLine(l)), [lines]);
  const debits = active.reduce((s, l) => s + (l.debit ?? 0), 0);
  const credits = active.reduce((s, l) => s + (l.credit ?? 0), 0);
  const diff = debits - credits;
  const bothSides = active.some((l) => (l.debit ?? 0) > 0 && (l.credit ?? 0) > 0);
  const incomplete = active.some((l) => !l.account_id || ((l.debit ?? 0) <= 0 && (l.credit ?? 0) <= 0));
  const minLines = isOpening ? 1 : 2;
  const canPost = !busy && !bothSides && !incomplete && active.length >= minLines && (isOpening ? debits + credits > 0 : (diff === 0 && debits > 0)) && Boolean(date);

  function update(key: number, patch: Partial<DraftLine>) {
    setLines((ls) => ls.map((l) => (l.key === key ? { ...l, ...patch } : l)));
  }
  function remove(key: number) {
    setLines((ls) => (ls.length <= 1 ? ls : ls.filter((l) => l.key !== key)));
  }

  async function post() {
    setBusy(true);
    setErr(null);
    const payload: LineInput[] = active.map((l) => ({
      account_id: l.account_id,
      debit_cents: l.debit ?? 0,
      credit_cents: l.credit ?? 0,
      memo: l.memo.trim() || null,
      member: l.member || null,
    }));
    try {
      if (mode.kind === 'new') {
        await journal.post({ entry_date: date, memo: memo.trim(), lines: payload });
        onSaved('Posted.');
      } else if (mode.kind === 'edit') {
        await journal.update(mode.entry.id, { entry_date: date, memo: memo.trim(), lines: payload });
        onSaved('Saved.');
      } else {
        await journal.openingBalances(date, payload);
        onSaved('Opening balances recorded.');
      }
    } catch (e) {
      setErr(e);
    } finally {
      setBusy(false);
    }
  }

  const title = mode.kind === 'new' ? 'New entry' : mode.kind === 'edit' ? 'Change this entry' : 'Opening balances';

  return (
    <Section title={title}>
      {isOpening && (
        <Note>
          Enter each account's balance as of the date below: assets as debits, liabilities and equity as credits.
          Any difference lands in Opening balance equity (3900), so the entry always balances.
        </Note>
      )}
      {mode.kind === 'edit' && (
        <Note>Changing an entry replaces all of its lines. The month must be open.</Note>
      )}
      <div className="lp-form-grid" style={{ maxWidth: 760 }}>
        <Field label={isOpening ? 'As of' : 'Date'}><input className="lp-inp" type="date" value={date} onChange={(e) => setDate(e.target.value)} /></Field>
        {!isOpening && (
          <Field label="Memo" hint="What this entry is for, in a sentence."><input className="lp-inp" value={memo} onChange={(e) => setMemo(e.target.value)} /></Field>
        )}
      </div>

      <div className="lp-tbl-wrap">
        <table className="lp-tbl">
          <thead>
            <tr>
              <th style={{ minWidth: 260 }}>Account</th>
              <th className="num" style={{ minWidth: 140 }}>Debit</th>
              <th className="num" style={{ minWidth: 140 }}>Credit</th>
              <th>Member</th>
              <th style={{ minWidth: 180 }}>Line memo</th>
              <th></th>
            </tr>
          </thead>
          <tbody>
            {lines.map((l) => {
              const both = (l.debit ?? 0) > 0 && (l.credit ?? 0) > 0;
              return (
                <tr key={l.key}>
                  <td>
                    <AccountSelect value={l.account_id} onChange={(id) => update(l.key, { account_id: id })} accounts={accounts} />
                  </td>
                  <td className="num"><MoneyInput cents={l.debit} onChange={(c) => update(l.key, { debit: c })} /></td>
                  <td className="num"><MoneyInput cents={l.credit} onChange={(c) => update(l.key, { credit: c })} /></td>
                  <td>
                    <select className="lp-sel" value={l.member} onChange={(e) => update(l.key, { member: e.target.value as '' | Member })}>
                      <option value="">—</option>
                      {MEMBERS.map((m) => <option key={m} value={m}>{memberWords(m)}</option>)}
                    </select>
                  </td>
                  <td><input className="lp-inp" value={l.memo} onChange={(e) => update(l.key, { memo: e.target.value })} /></td>
                  <td style={{ whiteSpace: 'nowrap' }}>
                    {both && <span className="lp-tag lp-tag--bad">One side only</span>}{' '}
                    <button type="button" className="lp-btn lp-btn--quiet lp-btn--small" disabled={lines.length <= 1} onClick={() => remove(l.key)} aria-label="Remove line">Remove</button>
                  </td>
                </tr>
              );
            })}
            <tr className="total">
              <td>Totals</td>
              <td className="num"><Money cents={debits} /></td>
              <td className="num"><Money cents={credits} /></td>
              <td colSpan={3}>
                Difference: <strong><Money cents={diff} /></strong>
                {isOpening
                  ? (diff !== 0 ? <span className="muted"> → Opening balance equity</span> : null)
                  : (diff !== 0 ? <span className="lp-tag lp-tag--warn" style={{ marginLeft: 8 }}>Must be zero to post</span> : <span className="lp-tag lp-tag--good" style={{ marginLeft: 8 }}>Balanced</span>)}
              </td>
            </tr>
          </tbody>
        </table>
      </div>

      <div className="lp-row" style={{ marginTop: 12 }}>
        <button type="button" className="lp-btn lp-btn--small" onClick={() => setLines((ls) => [...ls, blankLine()])}>Add a line</button>
        {incomplete && <span className="lp-field-hint">Every line needs an account and an amount on one side.</span>}
        {!incomplete && !isOpening && active.length < 2 && <span className="lp-field-hint">An entry needs at least two lines.</span>}
      </div>

      <ErrorNote error={err} />

      <div className="lp-row" style={{ marginTop: 16 }}>
        <button type="button" className="lp-btn lp-btn--primary" disabled={!canPost} onClick={() => void post()}>
          {busy ? 'Posting…' : mode.kind === 'edit' ? 'Save changes' : isOpening ? 'Record opening balances' : 'Post'}
        </button>
        <button type="button" className="lp-btn lp-btn--quiet" disabled={busy} onClick={onCancel}>Cancel</button>
      </div>
    </Section>
  );
}
